/* Header menu panels and the header that steps aside while scrolling down (Round 3A; markup in sections/sp-header.liquid
   and snippets/sp-mega-panel.liquid, styles in sp-header.css).

   Panels (from 990px):
   - Mouse: a panel opens after the pointer has rested on its button for INTENT_MS (no accidental opening when the
     pointer only crosses the menu). While a panel is open, moving onto another button switches straight away, unless
     the pointer is travelling towards the open panel (inside the triangle from where it was to the panel's top
     corners), in which case the switch waits SAFE_MS, so a diagonal path down to the panel never flips it. Leaving the
     header and panel closes it after CLOSE_GRACE_MS, cancelled if the pointer comes back.
   - Click or tap on a button toggles its panel at once; Enter and Space do the same (buttons). Arrow Down opens the
     panel and moves into its first link; Escape closes it and returns focus to its button; focus leaving the header
     closes it. aria-expanded follows the panel.
   - Opening: [hidden] comes off, then is-open on the next frame (CSS fades in with an 8px rise, 300ms); the scrim dims
     the page and closes the panel on click. Closing: is-open goes, [hidden] returns when the fade's transitions finish.
     (The sheet turns opaque in 150ms and its content fades and rises over 300ms: sp-header.css.)
   Header on scroll: hidden (html.sp-head-hidden) after scrolling down past the header, shown again on any scroll up
   or near the top; never hidden while a panel, a dialog or keyboard focus is in it. The same handler sets
   html.sp-head-scrolled once the page is more than twice the header's height down (and clears it back within one
   header's height of the top), so the logo shows the mark instead of the wordmark (Round 11, sp-header.css).
   Reduced motion: the CSS makes every change instant; the timings below still guard against accidental opening. */
const INTENT_MS = 180;
const SAFE_MS = 260;
const CLOSE_GRACE_MS = 300;
const wide = matchMedia('(min-width: 990px)');
const reduced = matchMedia('(prefers-reduced-motion: reduce)');

function initMega(root) {
  const head = root.querySelector('[data-sp-head]') || root;
  if (!head || head.dataset.spMegaReady) return;
  head.dataset.spMegaReady = 'true';
  const triggers = [...head.querySelectorAll('[data-sp-mega-trigger]')];
  const scrim = head.querySelector('[data-sp-mega-scrim]');
  if (!triggers.length) return;
  const panelOf = (trigger) => document.getElementById(trigger.getAttribute('aria-controls'));
  let current = null;
  let openedBy = '';
  let openTimer = 0;
  let closeTimer = 0;
  let safeTimer = 0;
  const trail = [];

  // Closing: is-open goes, and [hidden] returns once the fade has finished (its own and its content's transitions, so no
  // duration is copied here; a fade cut short by opening again rejects, and is-open keeps the panel).
  const fadeOut = (element) => {
    element.classList.remove('is-open');
    const done = () => {
      if (!element.classList.contains('is-open')) element.hidden = true;
    };
    const running = reduced.matches ? [] : element.getAnimations?.({ subtree: true }) || [];
    if (!running.length) done();
    else Promise.all(running.map((animation) => animation.finished)).then(done, done);
  };
  const fadeIn = (element) => {
    element.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => element.classList.add('is-open')));
  };
  // Photos in a panel load lazily; ask for them as soon as the shopper shows interest.
  const warm = (trigger) =>
    panelOf(trigger)
      ?.querySelectorAll('img[loading="lazy"]')
      .forEach((img) => {
        img.loading = 'eager';
      });

  function open(trigger, { focusFirst = false, by = 'pointer' } = {}) {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    clearTimeout(safeTimer);
    openedBy = by;
    if (current === trigger) {
      if (focusFirst) panelOf(trigger)?.querySelector('a')?.focus();
      return;
    }
    const panel = panelOf(trigger);
    if (!panel) return;
    warm(trigger);
    if (current) {
      // Switching: the old panel goes at once, the new one takes its place without the page flickering.
      const old = panelOf(current);
      current.setAttribute('aria-expanded', 'false');
      old.classList.remove('is-open');
      old.hidden = true;
      panel.hidden = false;
      panel.classList.add('is-open');
    } else {
      fadeIn(panel);
      if (scrim) fadeIn(scrim);
      document.documentElement.classList.remove('sp-head-hidden');
    }
    trigger.setAttribute('aria-expanded', 'true');
    current = trigger;
    if (focusFirst) panel.querySelector('a')?.focus();
  }
  function close({ returnFocus = false } = {}) {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    clearTimeout(safeTimer);
    if (!current) return;
    const trigger = current;
    current = null;
    trigger.setAttribute('aria-expanded', 'false');
    fadeOut(panelOf(trigger));
    if (scrim) fadeOut(scrim);
    if (returnFocus) trigger.focus();
  }

  // Is the pointer heading into the open panel? The triangle from where it was ~80ms ago to the panel's top corners.
  function headingIntoPanel(x, y) {
    if (!current) return false;
    const rect = panelOf(current).getBoundingClientRect();
    const from = trail.find((p) => performance.now() - p.t <= 120) || trail[0];
    if (!from) return false;
    const a = { x: from.x, y: from.y };
    const b = { x: rect.left, y: rect.top };
    const c = { x: rect.right, y: rect.top };
    const sign = (p1, p2, p3) => (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
    const d1 = sign({ x, y }, a, b);
    const d2 = sign({ x, y }, b, c);
    const d3 = sign({ x, y }, c, a);
    const neg = d1 < 0 || d2 < 0 || d3 < 0;
    const pos = d1 > 0 || d2 > 0 || d3 > 0;
    return !(neg && pos) && y > from.y;
  }

  head.addEventListener(
    'pointermove',
    (event) => {
      if (event.pointerType !== 'mouse') return;
      trail.push({ x: event.clientX, y: event.clientY, t: performance.now() });
      while (trail.length > 12) trail.shift();
    },
    { passive: true },
  );

  triggers.forEach((trigger) => {
    trigger.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse' || !wide.matches) return;
      clearTimeout(closeTimer);
      clearTimeout(openTimer);
      clearTimeout(safeTimer);
      if (current && current !== trigger) {
        if (headingIntoPanel(event.clientX, event.clientY))
          safeTimer = setTimeout(() => {
            if (trigger.matches(':hover')) open(trigger);
          }, SAFE_MS);
        else open(trigger);
        return;
      }
      warm(trigger);
      openTimer = setTimeout(() => open(trigger), INTENT_MS);
    });
    trigger.addEventListener('pointerleave', () => {
      clearTimeout(openTimer);
    });
    // A click on a panel the pointer has just opened keeps it open (the shopper is confirming, not cancelling).
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      if (current === trigger && openedBy !== 'pointer') close();
      else open(trigger, { by: event.detail === 0 ? 'keyboard' : 'click' });
    });
    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        open(trigger, { focusFirst: true, by: 'keyboard' });
      }
    });
  });

  // Resting on a plain link of the menu (no panel) closes an open panel, unless the pointer is on its way into it.
  head.querySelectorAll('.sp-head-link:not([data-sp-mega-trigger])').forEach((link) => {
    link.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse' || !current) return;
      clearTimeout(safeTimer);
      if (headingIntoPanel(event.clientX, event.clientY))
        safeTimer = setTimeout(() => {
          if (link.matches(':hover')) close();
        }, SAFE_MS);
      else close();
    });
  });

  // Leaving the header and its panel (the scrim is not part of it) closes after a short grace; coming back cancels.
  const zone = head.querySelector('.sp-head');
  const insideZone = (target) =>
    target instanceof Node && (zone.contains(target) || triggers.some((t) => panelOf(t)?.contains(target)));
  head.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse' || !current) return;
    if (insideZone(event.relatedTarget)) return;
    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => close(), CLOSE_GRACE_MS);
  });
  head.addEventListener('pointerover', (event) => {
    if (insideZone(event.target)) clearTimeout(closeTimer);
  });
  scrim?.addEventListener('click', () => close());
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && current) {
      event.preventDefault();
      close({ returnFocus: true });
    }
  });
  head.addEventListener('focusout', (event) => {
    if (!current) return;
    const next = event.relatedTarget;
    if (next instanceof Node && head.contains(next)) return;
    close();
  });
  wide.addEventListener('change', () => close());
  // Once the page has loaded and the browser is idle, fetch the panels' few small photos too (laptops only), so a
  // panel never opens with an empty tile, even on its first hover.
  const warmAll = () => {
    if (wide.matches) triggers.forEach(warm);
  };
  const idle = () =>
    'requestIdleCallback' in window ? requestIdleCallback(warmAll, { timeout: 4000 }) : setTimeout(warmAll, 2500);
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
  window.addEventListener('pagehide', () => close());
}

/* The header slides away while scrolling down and returns on the way up. */
function initScrollHide() {
  const group = document.getElementById('header-group');
  if (!group || !group.querySelector('.sp-head') || group.dataset.spHideReady) return;
  group.dataset.spHideReady = 'true';
  const root = document.documentElement;
  let last = window.scrollY;
  let frame = 0;
  // Round 11: the logo's mark replaces the wordmark further down; the gap between the two limits stops it flickering.
  const logo = (y, headerBottom) => {
    if (y > headerBottom * 2) root.classList.add('sp-head-scrolled');
    else if (y <= headerBottom) root.classList.remove('sp-head-scrolled');
  };
  requestAnimationFrame(() => logo(window.scrollY, group.offsetHeight));
  const busy = () =>
    group.querySelector('[aria-expanded="true"]') ||
    document.querySelector('dialog[open]') ||
    group.matches(':focus-within:has(:focus-visible)');
  window.addEventListener(
    'scroll',
    () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const y = window.scrollY;
        const headerBottom = group.offsetHeight;
        logo(y, headerBottom);
        const delta = y - last;
        if (Math.abs(delta) < 6) return;
        if (delta > 0 && y > headerBottom * 2 && !busy()) root.classList.add('sp-head-hidden');
        else if (delta < 0 || y <= headerBottom) root.classList.remove('sp-head-hidden');
        last = y;
      });
    },
    { passive: true },
  );
  // Keyboard users always see where focus is.
  group.addEventListener('focusin', () => root.classList.remove('sp-head-hidden'));
}

/* Round 4: the see-through bar over the home hero (sp-header.css). An 8px marker at the very top of the page is watched
   by an IntersectionObserver (no scroll handler, no layout reads): while it is in view the bar is see-through; once
   the page has scrolled past it the bar is solid. Transitions switch on only after the page has loaded
   (.sp-head-ready), so a page opened or reloaded mid-way never fades. */
function initOverHero() {
  const wrap = document.querySelector('.sp-head-wrap.sp-head-over');
  if (!wrap || wrap.dataset.spOverReady) return;
  wrap.dataset.spOverReady = 'true';
  const marker = document.createElement('div');
  marker.setAttribute('aria-hidden', 'true');
  marker.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:8px;pointer-events:none;visibility:hidden';
  document.body.prepend(marker);
  new IntersectionObserver(([entry]) => {
    // The inline script in sp-header.liquid kept the bar solid if the browser put the scroll back before this ran; the
    // observer takes over from its scroll listener here.
    if (wrap.spEarlySolid) {
      removeEventListener('scroll', wrap.spEarlySolid);
      wrap.spEarlySolid = null;
    }
    wrap.classList.toggle('is-solid', !entry.isIntersecting);
  }).observe(marker);
  // The fade is switched on only once the page has loaded and the browser has put the scroll back (a reload part-way
  // down, Back, or an anchor link): until then every change is instant, so the bar never fades in over content.
  const ready = () =>
    setTimeout(
      () => requestAnimationFrame(() => requestAnimationFrame(() => wrap.classList.add('sp-head-ready'))),
      120,
    );
  if (document.readyState === 'complete') ready();
  else window.addEventListener('load', ready, { once: true });
}

/* Horizon's base.css scrolls the root smoothly, which also animates the browser putting the scroll back on a reload or
   Back and the jump to an anchor on arrival. Smooth scrolling starts once the page has loaded (sp-sport.css). */
const scrollReady = () => requestAnimationFrame(() => document.documentElement.classList.add('sp-scroll-ready'));
if (document.readyState === 'complete') scrollReady();
else window.addEventListener('load', () => setTimeout(scrollReady, 120), { once: true });

initMega(document);
initScrollHide();
initOverHero();
document.addEventListener('shopify:section:load', (event) => {
  initMega(event.target);
  initScrollHide();
  initOverHero();
});
